import vars from "../styles/vars"

// Splits a goal name, returning an array with the respective names or `null`
export const splitGoalName = name =>
  name ? name.split("-").map(goalName => goalName || null) : null

// Combines goal and subgoal names into one string (e.g. "S3-O4")
export const combineGoalNames = (goalName, subGoalName) =>
  subGoalName ? `${goalName}-${subGoalName}` : goalName

// Finds a goal by its name (e.g. "S3-O4") and returns that goal as an object
// Works on d3 partitions and on goal.json data
export const findGoal = (name, goals) => {
  if (!name) {
    return null
  }

  let result = null

  let findDeep = function (name, goals) {
    return goals.some(goal => {
      if (goal.name === name || (goal.data && goal.data.name === name)) {
        result = goal
        return true
      }

      if (goal.children) {
        return findDeep(name, goal.children)
      }

      return false
    })
  }

  findDeep(name, goals)

  return result
}

// Calculate KPI values and state for each goal, extend the data with it
export const extendGoalsByKPIData = goals => {
  if (!goals) {
    return null
  }

  let extendDeep = function (goals) {
    return goals.map(goal => {
      // clone goal object for modification
      let g = { ...goal }

      // deep extend for possible children
      if (g.children) {
        g = {
          ...g,
          children: extendDeep(g.children),
        }
      }

      // extend a single goal with KPI values and state
      return g.kpis
        ? {
            ...g,
            kpis: g.kpis.map(kpi => {
              const values = getKPIValues(kpi)
              const state = calculateKPIState(values, kpi.formula)

              return {
                ...kpi,
                values,
                state,
                timeframe: g.timeframe,
              }
            }),
          }
        : g
    })
  }

  return extendDeep(goals)
}

// Calculate goal state for each goal, extend the data with it
export const extendGoalsByState = goals => {
  if (!goals) {
    return null
  }

  let extendDeep = function (goals) {
    return goals.map(goal => {
      // clone goal object for modification
      let g = { ...goal }

      // deep extend for possible children
      if (g.children) {
        g = {
          ...g,
          children: extendDeep(g.children),
        }
      }

      // extend a single goal with its goal state
      const calculateableKPIs = g.kpis.filter(({ state }) => isNumber(state))

      return {
        ...g,
        state: g.kpis
          ? g.kpiMode === "worst"
            ? // worst kpi state = goal state
              calculateableKPIs.length
              ? calculateableKPIs.reduce(
                  (worst, { state, weight }) => Math.min(worst, state * weight),
                  1
                )
              : 0 // state for when no KPIs are calculatable (i.e. all are null or in-progress)
            : // sum up kpi states
              calculateableKPIs.reduce(
                (total, { state, weight }) => total + state * weight,
                0
              )
          : 1, // state for when no KPIs are present
      }
    })
  }

  return extendDeep(goals)
}

// Render a goal's name, wrapping all numbers with <sub>
export const renderGoalName = (goalIndex, svg = false) =>
  goalIndex.replace(
    /\d+/g,
    svg
      ? '<tspan baseline-shift="sub" style="font-size: 70%">$&</tspan>'
      : "<sub>$&</sub>"
  )

// Get values for a KPI
export const getKPIValues = kpi => {
  const year = kpi.years[0]
  const quarter = year.quarters[0]

  return {
    kpi,
    year,
    quarter,
  }
}

// Find KPI object for current year
export const findCurrentKPI = (kpi, date) =>
  kpi.years.find(({ year }) => year === date.getFullYear())

// Find KPI values object for current quarter
export const findCurrentKPIQuarter = (year, date) =>
  year.quarters.find(
    ({ quarter }) => quarter === Math.floor((date.getMonth() + 3) / 3)
  )

// Calulcate KPI state
export const calculateKPIState = (values, formula) => {
  // replace formula placeholders
  const replacedFormula = formula
    .replace("e", values.year.evr)
    .replace("t", values.year.target)

  // parse formula
  let formulaParts = replacedFormula.match(
    /([+-]?\d+(\.\d+)?)|in-progress|null/g
  )

  // check for in progress state
  if (formulaParts.some(part => part === "in-progress")) {
    return "in-progress"
  }

  // check for null state
  if (formulaParts.some(part => part === "null")) {
    return null
  }

  // convert parts into numbers
  formulaParts = formulaParts.map(num => +num)

  if (formulaParts && formulaParts.length > 1) {
    // division
    const quotient = formulaParts[0] / formulaParts[1]

    // catch division by zero
    // 0/0 = NaN => 1
    // 9/0 = Infinity => 1
    return isNaN(quotient) ? 1 : !isFinite(quotient) ? 1 : quotient
  } else if (formulaParts) {
    // single number
    return formulaParts[0]
  }

  return null
}

export const getStateColor = ({ state }) =>
  state >= 1
    ? vars.colors.status.green
    : state >= 0.8
    ? vars.colors.status.yellow
    : vars.colors.status.red

// Renders a blank value
export const renderValue = (value, unit) => {
  // Percentage
  if (unit === "%") {
    return `${roundToTwoDigits(value * 100)}`
  }

  return value
}

// Renders a value with an optional unit
export const renderValueWithUnit = (value, unit) => {
  // No unit
  if (!unit) {
    return value
  }

  // Percentage
  if (unit === "%") {
    return `${roundToTwoDigits(value * 100)}${unit}`
  }

  // Other units
  return `${value} ${unit}`
}

export const roundToTwoDigits = num =>
  Math.round((num + Number.EPSILON) * 100) / 100

// TODO: not really working for percentage values (40% gets rounded to 100%)
export const ceilNumber = num => {
  // dynamic precision, based on number of digits
  let precision = Math.pow(10, Math.round(num).toString().length - 1)
  return precision === 1
    ? num + 1
    : Math.ceil((num + Number.EPSILON) / precision) * precision
}

export const lightenDarkenColor = (colorCode, amount) => {
  var usePound = false

  if (colorCode[0] === "#") {
    colorCode = colorCode.slice(1)
    usePound = true
  }

  var num = parseInt(colorCode, 16)

  var r = (num >> 16) + amount

  if (r > 255) {
    r = 255
  } else if (r < 0) {
    r = 0
  }

  var b = ((num >> 8) & 0x00ff) + amount

  if (b > 255) {
    b = 255
  } else if (b < 0) {
    b = 0
  }

  var g = (num & 0x0000ff) + amount

  if (g > 255) {
    g = 255
  } else if (g < 0) {
    g = 0
  }

  return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16)
}

export const isNumber = n => !isNaN(parseFloat(n)) && !isNaN(n - 0)

let uniqueIdPrefixCount = 0
export const uniqueIdPrefix = () => `uuid-${uniqueIdPrefixCount++}-`
